import React from 'react';
import {
  ChevronUpDownIcon,
  ChevronUpIcon,
  ChevronDownIcon,
  EyeIcon,
  PauseCircleIcon,
  TrashIcon,
  Cog6ToothIcon,
  UserIcon
} from '@heroicons/react/24/outline';

export interface Tenant {
  id: string;
  first_name: string;
  last_name: string;
  full_name?: string;
  email: string;
  role?: string;
  subscription_status: string;
  subscription_tier?: string;
  created_at: string;
  last_login_at: string | null;
  monthly_fee?: number;
  tenant_name?: string;
  tenants_id?: string;
  // Extended properties for admin operations
  name?: string;
  domain?: string;
  users_count?: number;
  monthly_revenue?: number;
  last_activity?: string;
}

interface SortConfig {
  key: keyof Tenant | 'first_name' | 'last_name' | 'role' | 'monthly_fee' | 'last_login_at';
  direction: 'asc' | 'desc';
}

interface TenantTableProps {
  tenants: Tenant[];
  selectedTenants: string[];
  onSelectTenant: (id: string) => void;
  onSelectAll: (selected: boolean) => void;
  onViewTenant: (tenant: Tenant) => void;
  onSuspendTenant: (tenant: Tenant) => void;
  onDeleteTenant: (tenant: Tenant) => void;
  onManageSubscription: (tenant: Tenant) => void;
  onImpersonate?: (tenant: Tenant) => void;
  isLoading?: boolean;
  currentPage: number;
  totalPages: number;
  totalItems: number;
  itemsPerPage: number;
  onPageChange: (page: number) => void;
  onSort: (key: keyof Tenant | 'first_name' | 'last_name' | 'role' | 'monthly_fee' | 'last_login_at') => void;
  sortConfig?: SortConfig;
}

const TenantTable: React.FC<TenantTableProps> = ({
  tenants,
  selectedTenants,
  onSelectTenant,
  onSelectAll,
  onViewTenant,
  onSuspendTenant,
  onDeleteTenant,
  onManageSubscription,
  onImpersonate,
  isLoading = false,
  currentPage,
  totalPages,
  totalItems,
  itemsPerPage,
  onPageChange,
  onSort,
  sortConfig
}) => {
  const getStatusColor = (status: string): string => {
    switch (status) {
      case 'active':
        return 'bg-emerald-500/20 text-emerald-300 border border-emerald-500/30';
      case 'trial':
        return 'bg-blue-500/20 text-blue-300 border border-blue-500/30';
      case 'past_due':
        return 'bg-red-500/20 text-red-300 border border-red-500/30';
      case 'cancelled':
        return 'bg-slate-500/20 text-slate-300 border border-slate-500/30';
      case 'suspended':
        return 'bg-amber-500/20 text-amber-300 border border-amber-500/30';
      default:
        return 'bg-slate-500/20 text-slate-300 border border-slate-500/30';
    }
  };

  const getTierColor = (tier?: string): string => {
    switch (tier) {
      case 'starter':
        return 'bg-blue-500/20 text-blue-300 border border-blue-500/30';
      case 'professional':
        return 'bg-purple-500/20 text-purple-300 border border-purple-500/30';
      case 'enterprise':
        return 'bg-orange-500/20 text-orange-300 border border-orange-500/30';
      case 'custom':
        return 'bg-indigo-500/20 text-indigo-300 border border-indigo-500/30';
      default:
        return 'bg-slate-500/20 text-slate-300 border border-slate-500/30';
    }
  };

  const getSortIcon = (key: keyof Tenant | 'first_name' | 'last_name' | 'role' | 'monthly_fee' | 'last_login_at') => {
    if (sortConfig?.key !== key) {
      return <ChevronUpDownIcon className="h-4 w-4 text-slate-400" />;
    }
    
    return sortConfig.direction === 'asc' 
      ? <ChevronUpIcon className="h-4 w-4 text-slate-300" />
      : <ChevronDownIcon className="h-4 w-4 text-slate-300" />;
  };

  const formatCurrency = (amount?: number): string => {
    if (!amount) return '$0';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  const formatDate = (dateString: string | null): string => {
    if (!dateString) return 'Never';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const isAllSelected = selectedTenants.length === tenants.length && tenants.length > 0;
  const isPartiallySelected = selectedTenants.length > 0 && selectedTenants.length < tenants.length;

  const generatePageNumbers = () => {
    const pages = [];
    const showEllipsis = totalPages > 7;
    
    if (!showEllipsis) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      // Always show first page
      pages.push(1);
      
      // Show ellipsis or pages around current page
      if (currentPage <= 4) {
        for (let i = 2; i <= Math.min(5, totalPages - 1); i++) {
          pages.push(i);
        }
        if (totalPages > 5) pages.push('...');
      } else if (currentPage >= totalPages - 3) {
        pages.push('...');
        for (let i = Math.max(totalPages - 4, 2); i <= totalPages - 1; i++) {
          pages.push(i);
        }
      } else {
        pages.push('...');
        for (let i = currentPage - 1; i <= currentPage + 1; i++) {
          pages.push(i);
        }
        pages.push('...');
      }
      
      // Always show last page
      if (totalPages > 1) pages.push(totalPages);
    }
    
    return pages;
  };

  if (isLoading) {
    return (
      <div className="bg-slate-800/50 backdrop-blur-sm shadow-xl rounded-xl border border-slate-700/50">
        <div className="animate-pulse p-4 space-y-4">
          {[...Array(5)].map((_, i) => (
            <div key={i} className="h-16 bg-slate-700/50 rounded-xl"></div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="bg-slate-800/50 backdrop-blur-sm shadow-xl rounded-xl border border-slate-700/50">
      {/* Table */}
      <div className="overflow-x-auto">
        <table className="w-full divide-y divide-slate-700/50">
          <thead className="bg-slate-900/50">
            <tr>
              <th scope="col" className="relative px-6 py-3">
                <input
                  type="checkbox"
                  checked={isAllSelected}
                  ref={(input) => {
                    if (input) input.indeterminate = isPartiallySelected;
                  }}
                  onChange={(e) => onSelectAll(e.target.checked)}
                  className="absolute left-4 top-1/2 -mt-2 h-4 w-4 rounded border-slate-600 bg-slate-700 text-indigo-500 focus:ring-indigo-500/50"
                />
              </th>
              
              <th 
                scope="col" 
                className="px-6 py-3 text-left text-xs font-medium text-slate-400 uppercase tracking-wider cursor-pointer hover:bg-slate-800/50 transition-colors duration-200"
                onClick={() => onSort('first_name')}
              >
                <div className="flex items-center space-x-1">
                  <span>User</span>
                  {getSortIcon('first_name')}
                </div>
              </th>
              
              <th 
                scope="col" 
                className="px-6 py-3 text-left text-xs font-medium text-slate-400 uppercase tracking-wider cursor-pointer hover:bg-slate-800/50 transition-colors duration-200"
                onClick={() => onSort('subscription_status')}
              >
                <div className="flex items-center space-x-1">
                  <span>Status</span>
                  {getSortIcon('subscription_status')}
                </div>
              </th>
              
              <th 
                scope="col" 
                className="px-6 py-3 text-left text-xs font-medium text-slate-400 uppercase tracking-wider cursor-pointer hover:bg-slate-800/50 transition-colors duration-200"
                onClick={() => onSort('subscription_tier')}
              >
                <div className="flex items-center space-x-1">
                  <span>Tier</span>
                  {getSortIcon('subscription_tier')}
                </div>
              </th>
              
              <th 
                scope="col" 
                className="px-6 py-3 text-left text-xs font-medium text-slate-400 uppercase tracking-wider cursor-pointer hover:bg-slate-800/50 transition-colors duration-200"
                onClick={() => onSort('role')}
              >
                <div className="flex items-center space-x-1">
                  <span>Role</span>
                  {getSortIcon('role')}
                </div>
              </th>
              
              <th 
                scope="col" 
                className="px-6 py-3 text-left text-xs font-medium text-slate-400 uppercase tracking-wider cursor-pointer hover:bg-slate-800/50 transition-colors duration-200"
                onClick={() => onSort('monthly_fee')}
              >
                <div className="flex items-center space-x-1">
                  <span>Monthly Fee</span>
                  {getSortIcon('monthly_fee')}
                </div>
              </th>
              
              <th 
                scope="col" 
                className="px-6 py-3 text-left text-xs font-medium text-slate-400 uppercase tracking-wider cursor-pointer hover:bg-slate-800/50 transition-colors duration-200"
                onClick={() => onSort('last_login_at')}
              >
                <div className="flex items-center space-x-1">
                  <span>Last Login</span>
                  {getSortIcon('last_login_at')}
                </div>
              </th>
              
              <th scope="col" className="relative px-6 py-3">
                <span className="sr-only">Actions</span>
              </th>
            </tr>
          </thead>
          <tbody className="bg-slate-800/30 divide-y divide-slate-700/50">
            {tenants.map((tenant) => (
              <tr 
                key={tenant.id} 
                className={`hover:bg-slate-700/30 transition-colors duration-200 ${selectedTenants.includes(tenant.id) ? 'bg-indigo-500/20' : ''}`}
              >
                <td className="relative px-6 py-4">
                  <input
                    type="checkbox"
                    checked={selectedTenants.includes(tenant.id)}
                    onChange={() => onSelectTenant(tenant.id)}
                    className="absolute left-4 top-1/2 -mt-2 h-4 w-4 rounded border-slate-600 bg-slate-700 text-indigo-500 focus:ring-indigo-500/50"
                  />
                </td>
                
                <td className="px-6 py-4 whitespace-nowrap">
                  <div>
                    <div className="text-sm font-medium text-white">
                      {tenant.full_name || `${tenant.first_name} ${tenant.last_name}` || 'No Name'}
                    </div>
                    <div className="text-sm text-slate-400">{tenant.email}</div>
                    {tenant.tenant_name && (
                      <div className="text-xs text-slate-500">Tenant: {tenant.tenant_name}</div>
                    )}
                  </div>
                </td>
                
                <td className="px-6 py-4 whitespace-nowrap">
                  <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(tenant.subscription_status)}`}>
                    {tenant.subscription_status}
                  </span>
                </td>
                
                <td className="px-6 py-4 whitespace-nowrap">
                  {tenant.subscription_tier && (
                    <span className={`px-2 py-1 text-xs font-medium rounded-full ${getTierColor(tenant.subscription_tier)}`}>
                      {tenant.subscription_tier}
                    </span>
                  )}
                </td>
                
                <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-300">
                  {tenant.role || 'user'}
                </td>
                
                <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-300">
                  {formatCurrency(tenant.monthly_fee || 0)}
                </td>
                
                <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-400">
                  {formatDate(tenant.last_login_at)}
                </td>
                
                <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                  <div className="flex items-center space-x-2">
                    {onImpersonate && (
                      <button
                        onClick={() => onImpersonate(tenant)}
                        className="text-cyan-400 hover:text-cyan-300 transition-colors duration-200"
                        title="Impersonate User"
                      >
                        <UserIcon className="h-4 w-4" />
                      </button>
                    )}
                    <button
                      onClick={() => onViewTenant(tenant)}
                      className="text-indigo-400 hover:text-indigo-300 transition-colors duration-200"
                      title="View Details"
                    >
                      <EyeIcon className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => onManageSubscription(tenant)}
                      className="text-purple-400 hover:text-purple-300 transition-colors duration-200"
                      title="Manage Subscription"
                    >
                      <Cog6ToothIcon className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => onSuspendTenant(tenant)}
                      className="text-amber-400 hover:text-amber-300 transition-colors duration-200"
                      title="Suspend"
                    >
                      <PauseCircleIcon className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => onDeleteTenant(tenant)}
                      className="text-red-400 hover:text-red-300 transition-colors duration-200"
                      title="Delete"
                    >
                      <TrashIcon className="h-4 w-4" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <div className="bg-slate-800/30 px-4 py-3 flex items-center justify-between border-t border-slate-700/50 sm:px-6">
        <div className="flex-1 flex justify-between items-center">
          <div>
            <p className="text-sm text-slate-300">
              Showing{' '}
              <span className="font-medium">
                {Math.min((currentPage - 1) * itemsPerPage + 1, totalItems)}
              </span>{' '}
              to{' '}
              <span className="font-medium">
                {Math.min(currentPage * itemsPerPage, totalItems)}
              </span>{' '}
              of{' '}
              <span className="font-medium">{totalItems}</span> results
            </p>
          </div>
          
          <div>
            <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
              <button
                onClick={() => onPageChange(currentPage - 1)}
                disabled={currentPage === 1}
                className="relative inline-flex items-center px-2 py-2 rounded-l-xl border border-slate-600/50 bg-slate-700/50 text-sm font-medium text-slate-300 hover:bg-slate-600/50 hover:text-white disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
              >
                Previous
              </button>
              
              {generatePageNumbers().map((page, index) => (
                <React.Fragment key={index}>
                  {page === '...' ? (
                    <span className="relative inline-flex items-center px-4 py-2 border border-slate-600/50 bg-slate-700/50 text-sm font-medium text-slate-300">
                      ...
                    </span>
                  ) : (
                    <button
                      onClick={() => onPageChange(page as number)}
                      className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium transition-all duration-200 ${
                        currentPage === page
                          ? 'z-10 bg-indigo-500/20 border-indigo-500/30 text-indigo-300'
                          : 'bg-slate-700/50 border-slate-600/50 text-slate-300 hover:bg-slate-600/50 hover:text-white'
                      }`}
                    >
                      {page}
                    </button>
                  )}
                </React.Fragment>
              ))}
              
              <button
                onClick={() => onPageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
                className="relative inline-flex items-center px-2 py-2 rounded-r-xl border border-slate-600/50 bg-slate-700/50 text-sm font-medium text-slate-300 hover:bg-slate-600/50 hover:text-white disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
              >
                Next
              </button>
            </nav>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TenantTable;